import React, { useEffect, useState } from 'react';
import AppLayout from '../../components/layout/AppLayout';
import { PageHeader, EmptyState } from '../../components/common/PageHeader';
import api from '../../lib/api';
import { Plus, Edit2, Trash2, ShieldCheck, Users, Key, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const roleLabel = (r) => r === 'admin' ? 'Administrator' : r === 'pm' ? 'Projektmanager' : r;
const statusLabel = (s) => s === 'active' ? 'Aktiv' : s === 'inactive' ? 'Inaktiv' : s;

const emptyForm = { email: '', name: '', role: 'pm', password: '', new_password: '', status: 'active' };

const AdminUsers = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // user_id or null for new
  const [form, setForm] = useState(emptyForm);
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const r = await api.get('/admin/users');
      setUsers(r.data);
    } catch (err) {
      toast.error('Fehler beim Laden');
    }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowPw(false);
    setDialogOpen(true);
  };

  const openEdit = (u) => {
    setEditing(u.user_id);
    setForm({ email: u.email, name: u.name || '', role: u.role, password: '', new_password: '', status: u.status || 'active' });
    setShowPw(false);
    setDialogOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editing) {
        const payload = { email: form.email, name: form.name, role: form.role, status: form.status };
        if (form.new_password) payload.new_password = form.new_password;
        await api.put(`/admin/users/${editing}`, payload);
        toast.success('Benutzer aktualisiert');
      } else {
        if (!form.password || form.password.length < 6) {
          toast.error('Passwort muss mindestens 6 Zeichen lang sein');
          setLoading(false);
          return;
        }
        await api.post('/admin/users', { email: form.email, name: form.name, role: form.role, password: form.password });
        toast.success('Benutzer angelegt');
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Fehler');
    } finally {
      setLoading(false);
    }
  };

  const del = async (u) => {
    if (!window.confirm(`Benutzer "${u.email}" wirklich löschen?`)) return;
    try {
      await api.delete(`/admin/users/${u.user_id}`);
      toast.success('Benutzer gelöscht');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Fehler');
    }
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!#$%&';
    let pw = '';
    for (let i = 0; i < 14; i++) pw += chars[Math.floor(Math.random() * chars.length)];
    if (editing) setForm(f => ({ ...f, new_password: pw }));
    else setForm(f => ({ ...f, password: pw }));
    setShowPw(true);
    toast.success('Zufälliges Passwort generiert - bitte kopieren!');
  };

  return (
    <AppLayout>
      <PageHeader title="Benutzerverwaltung" description="Administratoren und Projektmanager verwalten">
        <button onClick={openNew} data-testid="new-user-btn" className="bg-[#001d4c] hover:bg-[#0A365C] text-white font-medium rounded-md px-4 py-2 text-sm inline-flex items-center gap-2">
          <Plus className="w-4 h-4" /> Neuer Benutzer
        </button>
      </PageHeader>

      {users.length === 0 ? (
        <EmptyState icon={Users} title="Keine Benutzer" description="Legen Sie den ersten PM oder Admin an." />
      ) : (
        <div className="bg-white border border-slate-200 rounded-md card-subtle overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">Name</th>
                <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">E-Mail</th>
                <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">Rolle</th>
                <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">Status</th>
                <th className="text-right text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.user_id} className="hover:bg-slate-50" data-testid={`user-row-${u.user_id}`}>
                  <td className="border-b border-slate-100 py-3 px-4 text-sm text-slate-900 font-medium">
                    {u.name || '-'}
                    {u.user_id === currentUser?.user_id && <span className="ml-2 text-xs text-slate-400">(Sie)</span>}
                  </td>
                  <td className="border-b border-slate-100 py-3 px-4 text-sm text-slate-700">{u.email}</td>
                  <td className="border-b border-slate-100 py-3 px-4">
                    <span className={`inline-flex items-center gap-1 text-xs font-medium rounded-full px-2 py-0.5 ${u.role === 'admin' ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'}`}>
                      {u.role === 'admin' && <ShieldCheck className="w-3 h-3" />}
                      {roleLabel(u.role)}
                    </span>
                  </td>
                  <td className="border-b border-slate-100 py-3 px-4">
                    <span className={`text-xs font-medium ${u.status === 'active' ? 'text-emerald-700' : 'text-slate-400'}`}>{statusLabel(u.status)}</span>
                  </td>
                  <td className="border-b border-slate-100 py-3 px-4 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={() => openEdit(u)} data-testid={`edit-user-${u.user_id}`} className="text-slate-500 hover:text-[#001d4c] p-1.5 rounded hover:bg-slate-100" title="Bearbeiten">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      {u.user_id !== currentUser?.user_id && (
                        <button onClick={() => del(u)} data-testid={`delete-user-${u.user_id}`} className="text-slate-500 hover:text-red-600 p-1.5 rounded hover:bg-slate-100" title="Löschen">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {dialogOpen && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4 overflow-y-auto" onClick={() => setDialogOpen(false)}>
          <div className="bg-white rounded-md max-w-md w-full p-6 my-8" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-lg font-semibold text-slate-900 mb-4">
              {editing ? 'Benutzer bearbeiten' : 'Neuer Benutzer'}
            </h3>
            <form onSubmit={save} className="space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Name *</label>
                <input required value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} data-testid="user-name-input" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">E-Mail *</label>
                <input type="email" required value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} data-testid="user-email-input" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Rolle *</label>
                <select value={form.role} onChange={(e) => setForm(f => ({ ...f, role: e.target.value }))} data-testid="user-role-select" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none">
                  <option value="pm">Projektmanager (PM)</option>
                  <option value="admin">Administrator</option>
                </select>
              </div>
              {editing && (
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Status</label>
                  <select value={form.status} onChange={(e) => setForm(f => ({ ...f, status: e.target.value }))} data-testid="user-status-select" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none">
                    <option value="active">Aktiv</option>
                    <option value="inactive">Inaktiv</option>
                  </select>
                </div>
              )}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {editing ? 'Neues Passwort (optional)' : 'Passwort *'}
                  </label>
                  <button type="button" onClick={generatePassword} data-testid="generate-pw-btn" className="text-xs text-[#001d4c] hover:underline inline-flex items-center gap-1">
                    <Key className="w-3 h-3" /> Zufällig generieren
                  </button>
                </div>
                <div className="relative">
                  <input
                    type={showPw ? 'text' : 'password'}
                    required={!editing}
                    minLength={6}
                    value={editing ? form.new_password : form.password}
                    onChange={(e) => setForm(f => editing ? { ...f, new_password: e.target.value } : { ...f, password: e.target.value })}
                    data-testid="user-password-input"
                    className="w-full border border-slate-300 rounded-md px-3 py-2 pr-10 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none font-mono"
                    placeholder={editing ? 'Leer lassen zum Beibehalten' : 'Mindestens 6 Zeichen'}
                  />
                  <button type="button" onClick={() => setShowPw(p => !p)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 p-1" title={showPw ? 'Verbergen' : 'Anzeigen'}>
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {!editing && (
                  <p className="text-xs text-slate-400 mt-1">Der Benutzer kann sich sofort mit diesem Passwort einloggen und es später selbst über „Passwort vergessen?" ändern.</p>
                )}
              </div>

              <div className="flex gap-2 pt-2 justify-end">
                <button type="button" onClick={() => setDialogOpen(false)} className="px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-md">Abbrechen</button>
                <button type="submit" disabled={loading} data-testid="save-user-btn" className="bg-[#001d4c] hover:bg-[#0A365C] text-white font-medium rounded-md px-4 py-2 text-sm disabled:opacity-50">
                  {loading ? 'Speichern...' : editing ? 'Änderungen speichern' : 'Benutzer anlegen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default AdminUsers;
